import {
  getCreateAuthSessionsEndpoint,
  getDeviceAuthAudiences,
  getDeviceAuthOidcClientId,
  getDeviceAuthTokenEndpoint,
} from '~/config'
import { useIpRestrictionStore } from '~/stores/ipRestriction/ipRestriction'
import { getAppModeHeader } from './headers'

/**
 * キオスク端末アカウント (Device) 認証のセッション操作ユーティリティ。
 *
 * Rump の mTLS トークンエンドポイント（クライアント証明書による認証）で access_token を取得し、
 * harami_api の `/auth/sessions` に渡してデバイスセッション Cookie を発行する。
 * セッションの有効期限は Cookie `harami_expire_at`（UNIX 秒）で表現される。
 */

/**
 * Cookie 値を取得する。存在しなければ null。
 *
 * `;` 区切り・各エントリの前後空白・値に `=` を含むケースに対応するため、
 * 区切りは `;` で分割して trim し、キーと値は最初の `=` で分離する。
 */
export const getCookieValue = (name: string): string | null => {
  for (const entry of document.cookie.split(';')) {
    const trimmed = entry.trim()
    const eqIndex = trimmed.indexOf('=')
    if (eqIndex === -1) continue
    if (trimmed.slice(0, eqIndex) === name) {
      return trimmed.slice(eqIndex + 1)
    }
  }
  return null
}

/** デバイスセッション（harami_expire_at Cookie）が存在するか。 */
export const hasDeviceAuthSession = (): boolean => {
  return getCookieValue('harami_expire_at') !== null
}

/**
 * fetchSession の失敗理由。
 * - ip_address_restricted: Rump の IP アドレス制限でトークン取得が拒否された
 * - unknown: それ以外（証明書なし・ネットワークエラー・/auth/sessions 失敗 等）
 */
type FetchSessionFailureReason = 'ip_address_restricted' | 'unknown'

export type FetchSessionResult =
  | { success: true }
  | { success: false; reason: FetchSessionFailureReason }

/**
 * Rump トークンエンドポイントのレスポンスが IP アドレス制限エラーかどうか。
 * IP 制限違反時は 403 + ボディの `error` に RFC6749 拡張エラーコード
 * `ip_address_restricted` が入る。ボディが JSON でない・読めない場合は
 * false（unknown 扱い）に倒す。
 */
const isIpAddressRestrictedResponse = async (
  response: Response,
): Promise<boolean> => {
  if (response.status !== 403) {
    return false
  }
  const body: unknown = await response.json().catch(() => null)
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    body.error === 'ip_address_restricted'
  )
}

const buildDeviceTokenRequestBody = () => {
  const body = new URLSearchParams({
    grant_type: 'client_credentials',
    client_id: getDeviceAuthOidcClientId(),
    scope: 'device_profile',
  })
  for (const audience of getDeviceAuthAudiences()) {
    body.append('audience', audience)
  }
  return body
}

const fetchSessionInternal = async (): Promise<FetchSessionResult> => {
  try {
    const tokenResponse = await fetch(getDeviceAuthTokenEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: buildDeviceTokenRequestBody(),
      credentials: 'include',
    })
    if (!tokenResponse.ok) {
      if (await isIpAddressRestrictedResponse(tokenResponse)) {
        // グローバルの IP 制限エラー画面（オーバーレイ）を表示させる。
        // トークン取得は本関数に集約されているため、ここで検知すれば全経路をカバーできる。
        useIpRestrictionStore.getState().ipRestrictionDetected()
        return { success: false, reason: 'ip_address_restricted' }
      }
      return { success: false, reason: 'unknown' }
    }

    const token: { access_token: string; expires_in: number } =
      await tokenResponse.json()

    // アプリモード宣言ヘッダーは harami_api へのリクエストにのみ付与する。
    // Rump のトークンエンドポイントは別サービスであり、CORS で許可されていないヘッダーを
    // 付けるとプリフライトが失敗するため付与しない。
    const sessionResponse = await fetch(getCreateAuthSessionsEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAppModeHeader() },
      credentials: 'include',
      body: JSON.stringify({
        accessToken: token.access_token,
        expiresIn: token.expires_in,
      }),
    })
    if (!sessionResponse.ok) {
      return { success: false, reason: 'unknown' }
    }

    // フラグ解除はセッション確立まで完了したときのみ行う。
    // トークン取得成功の時点で解除すると、直後の /auth/sessions 失敗時に
    // オーバーレイが先に消えて再試行のエラー表示が出せず、かつ
    // useDeviceAuthSession のキャッシュ（ip_address_restricted）が残ったまま
    // /me が無効化され続けて復旧不能になるため。
    useIpRestrictionStore.getState().ipRestrictionResolved()
    return { success: true }
  } catch {
    return { success: false, reason: 'unknown' }
  }
}

// 実行中の fetchSession。呼び出し元（middleware の自動リフレッシュ・エラー画面の再試行・
// 初回セッション確立）が並行しても同一の実行を待たせ、トークンエンドポイントへの
// 二重 POST と、完了順による IP 制限フラグの上書き競合を防ぐ。
let inFlightSession: Promise<FetchSessionResult> | null = null

/**
 * デバイスセッションを作成する。
 * Rump mTLS でアクセストークンを取得し、`/auth/sessions` に渡してセッション Cookie を発行する。
 * 成功・失敗を `{ success }` で返す（呼び出し側でフォールバックを判断する）。
 * 実行中の呼び出しがある場合は新たに実行せず、その結果を返す。
 */
export const fetchSession = (): Promise<FetchSessionResult> => {
  if (inFlightSession) {
    return inFlightSession
  }
  inFlightSession = fetchSessionInternal().finally(() => {
    inFlightSession = null
  })
  return inFlightSession
}
